import {
  report,
  REPORT_TYPES,
  createPerformanceTracker,
  TRACKING_ACTIONS,
  TrackingData,
  PerformanceTrackingAbortReasons,
} from '@adsk/exo-skeleton-api';
import { getRemoteRuntime } from '@adsk/exo-skeleton-api/internal';

import getDiffInSecondsBetweenTwoDates from './getDiffInSecondsBetweenTwoDates';
import { EXO_SKELETON_SCOPE_NAME } from '../consts';

type TrackingMap = Map<
  string,
  Map<
    string,
    {
      startDate: Date;
      endDate?: Date;
    }
  >
>;

const trackingMap: TrackingMap = new Map();

export const PERFORMANCE_TRACKING_ACTION_NAME = 'performance-tracking';
export const PERFORMANCE_TRACKING_ABORTED_BY_UNMOUNT =
  'performance-tracking-aborted-by-unmount';
export const PERFORMANCE_TRACKING_ABORTED_BY_TIMEOUT =
  'performance-tracking-aborted-by-timeout';
export const PERFORMANCE_TRACKING_END_WITH_NO_START =
  'performance-tracking-end-with-no-start';

const getMapKey = ({
  scope,
  moduleName,
}: {
  scope: string;
  moduleName?: string;
}) => `${scope}_${moduleName}`;

// For testing purposes only
export function UNSAFE_getTrackingMapForTests(): TrackingMap {
  return trackingMap;
}

export function startTracking({
  scope,
  trackingId,
  date,
  moduleName,
}: Pick<TrackingData, 'scope' | 'moduleName' | 'trackingId'> & {
  date: Date;
}) {
  const key = getMapKey({ scope, moduleName });

  if (!trackingMap.has(key)) {
    trackingMap.set(key, new Map());
  }

  if (trackingMap.get(key)!.has(trackingId)) {
    console.warn(
      `trackingId: '${trackingId}' already exists for scope '${scope}' and module '${moduleName}', overriding the previous startDate may cause your final report to be inaccurate.`,
    );
  }

  trackingMap.get(key)!.set(trackingId, { startDate: date });
}

export function abortTracking({
  scope,
  moduleName,
  trackingId,
  reason,
}: Pick<TrackingData, 'scope' | 'moduleName' | 'trackingId'> & {
  reason?: PerformanceTrackingAbortReasons;
}) {
  if (!reason) {
    console.error('reason is required to abort tracking');
  }

  const key = getMapKey({ scope, moduleName });

  if (trackingMap.has(key) && trackingMap.get(key)!.has(trackingId)) {
    const startDate = trackingMap.get(key)!.get(trackingId)!.startDate;
    const { moniker, override } = getRemoteRuntime(scope) || {};

    return report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: reason === 'timeout' ? REPORT_TYPES.warning : REPORT_TYPES.info,
      value:
        reason === 'timeout'
          ? PERFORMANCE_TRACKING_ABORTED_BY_TIMEOUT
          : PERFORMANCE_TRACKING_ABORTED_BY_UNMOUNT,
      context: {
        moniker,
        override,
        scope,
        trackingId,
        moduleName,
        startDate,
      },
    }).finally(() => {
      trackingMap.get(key)!.delete(trackingId);
    });
  }

  return undefined;
}

export function endTracking({
  scope,
  trackingId,
  parentTrackingId,
  moduleName,
  metadata,
  date,
}: Pick<
  TrackingData,
  'scope' | 'trackingId' | 'metadata' | 'moduleName' | 'parentTrackingId'
> & {
  date: Date;
}) {
  const key = getMapKey({ scope, moduleName });

  const startDate = trackingMap.get(key)?.get(trackingId)?.startDate;
  const { moniker, override } = getRemoteRuntime(scope) || {};

  if (!startDate) {
    return report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.info,
      value: PERFORMANCE_TRACKING_END_WITH_NO_START,
      context: {
        moniker,
        override,
        scope,
        moduleName,
        trackingId,
        parentTrackingId,
        endDate: date,
      },
    });
  }

  const totalTimeInSeconds = getDiffInSecondsBetweenTwoDates(startDate, date);

  return report({
    scope: EXO_SKELETON_SCOPE_NAME,
    type: REPORT_TYPES.info,
    value: PERFORMANCE_TRACKING_ACTION_NAME,
    context: {
      moniker,
      override,
      scope,
      parentTrackingId,
      trackingId,
      startDate,
      moduleName,
      metadata,
      endDate: date,
      totalTimeInSeconds,
      totalTimeInSecondsFormatted: totalTimeInSeconds + 's',
    },
  }).finally(() => {
    trackingMap.get(key)!.delete(trackingId);
  });
}

export function initializeTrackingManager() {
  return createPerformanceTracker({
    onTrack(data) {
      const { action } = data.data;

      switch (action) {
        case TRACKING_ACTIONS.START: {
          return startTracking({ ...data.data, date: new Date() });
        }
        case TRACKING_ACTIONS.END: {
          return endTracking({ ...data.data, date: new Date() });
        }
        case TRACKING_ACTIONS.ABORT: {
          return abortTracking({
            ...data.data,
            reason: data.data.metadata
              ?.reason as PerformanceTrackingAbortReasons,
          });
        }
        default: {
          console.error(`Unknown action provided for tracking: ${action}`);
          return null;
        }
      }
    },
  });
}
